import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useNotify } from "../../../context/NotificationContext";
import { Card, Table, Input, Button, Select, Typography, Tag } from "antd";
import { PlusOutlined, SearchOutlined, EditOutlined } from "@ant-design/icons";
import masterService from "../../../services/masterService";
import { getImageUrl } from "../../../utils/commonFunctions";
import { useCommon } from "../../../context/CommonContext";

const { Option } = Select;
const { Title, Text } = Typography;
const { Search } = Input;

const CountryList = () => {
  const navigate = useNavigate();
  const { setPageTitle } = useCommon();
  const notify = useNotify();
  // State management
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) =>
      `${range[0]}-${range[1]} of ${total} countries`,
  });

  // Filters. mobVisible/hasFlag come from the table's column filter menus and
  // are applied SERVER-side — the list is paginated, so filtering only the
  // visible page client-side would silently lie about the data.
  const [filters, setFilters] = useState({
    searchText: "",
    status: "all",
    mobVisible: null, // "true" | "false" | null (no filter)
    hasFlag: null, // "true" | "false" | null (no filter)
  });

  // Typing fired one request per keystroke. The term is now searched once the
  // typing settles, on the same 300ms the users and promos lists use.
  const searchDebounceRef = useRef(null);
  const SEARCH_DEBOUNCE_MS = 300;
  // Monotonic request id — responses from superseded requests are dropped so a
  // slow earlier search can't overwrite the results of the latest one.
  const requestSeqRef = useRef(0);

  const getCountries = useCallback(
    async (params = {}) => {
      const requestId = ++requestSeqRef.current;
      try {
        setLoading(true);

        const requestParams = {
          perPage: pagination.pageSize,
          currentPage: pagination.current,
          searchText: filters.searchText,
          // Always sent, so searching or paging can no longer silently drop an
          // active status filter. The backend ignores anything but Y/N.
          status: filters.status,
          mobVisible: filters.mobVisible,
          hasFlag: filters.hasFlag,
          ...params,
        };

        const response = await masterService.getCountries(requestParams);
        if (requestId !== requestSeqRef.current) return; // superseded by a newer request

        if (response.data && response.data.status) {
          // Handle different possible response structures
          const countriesData =
            response.data.data?.data || response.data.data || [];
          const paginationData =
            response.data.data?.pagination || response.data.pagination || {};

          // Ensure countriesData is an array
          if (Array.isArray(countriesData)) {
            setCountries(countriesData);
          } else {
            console.error("Countries data is not an array:", countriesData);
            setCountries([]);
          }

          setPagination((prev) => ({
            ...prev,
            ...paginationData,
            current: paginationData.currentPage || 1,
          }));
        } else {
          notify.error({ message: "Failed to fetch countries" });
          setCountries([]);
        }
      } catch (error) {
        if (requestId !== requestSeqRef.current) return;
        console.error("Error fetching countries:", error);
        notify.error({ message: "Failed to fetch countries" });
        setCountries([]);
      } finally {
        // Only the newest request clears the spinner — an overtaken one finishing
        // early would otherwise show the list as loaded while it is still loading.
        if (requestId === requestSeqRef.current) setLoading(false);
      }
    },
    [filters.searchText, filters.status, filters.mobVisible, filters.hasFlag]
  );

  // Load countries data
  useEffect(() => {
    setPageTitle("Countries");
    getCountries();
  }, [setPageTitle]);

  const handleSearchChange = (e) => {
    const value = e.target.value ?? "";
    setFilters((prev) => ({ ...prev, searchText: value }));
    setPagination((prev) => ({ ...prev, current: 1 }));
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      getCountries({
        searchText: value.trim(),
        currentPage: 1,
      });
      searchDebounceRef.current = null;
    }, SEARCH_DEBOUNCE_MS);
  };

  // Enter key / search icon applies immediately, cancelling any pending debounce
  // so the term is not searched twice.
  const handleSearchNow = () => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    getCountries({ searchText: (filters.searchText || "").trim(), currentPage: 1 });
  };

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, []);
  // Handle status filter change
  const handleStatusChange = (value) => {
    setFilters((prev) => ({ ...prev, status: value }));
    setPagination((prev) => ({ ...prev, current: 1 }));
    getCountries({
      searchText: filters.searchText,
      status: value,
      perPage: pagination.pageSize,
      currentPage: 1,
    });
  };

  // Handle table change (pagination, sorting, filtering)
  const handleTableChange = (paginationInfo, tableFilters, sorter) => {
    const { current, pageSize } = paginationInfo;

    // Column filter menus (single-select) — antd hands the checked values back
    // keyed by column key; the backend does the actual filtering. Status shares
    // its state with the header dropdown, so both controls stay in sync.
    const mobVisible = tableFilters?.country_mob_code_visible?.[0] ?? null;
    const hasFlag = tableFilters?.flag?.[0] ?? null;
    const status = tableFilters?.country_is_active?.[0] ?? "all";

    setFilters((prev) => ({ ...prev, status, mobVisible, hasFlag }));
    setPagination((prev) => ({
      ...prev,
      current,
      pageSize,
    }));

    getCountries({
      searchText: filters.searchText,
      status,
      mobVisible,
      hasFlag,
      perPage: pageSize,
      currentPage: current,
    });
  };

  // Handle add country
  const handleAddCountry = () => {
    navigate("/countries/add/new");
  };

  // Handle edit country
  const handleEditCountry = (countryId) => {
    navigate(`/countries/edit/${countryId}`);
  };

  // Table columns
  const columns = [
    {
      title: "Country Name",
      dataIndex: "country_name",
      key: "country_name",
      defaultSortOrder: "ascend",
      sorter: (a, b) => a.country_name.localeCompare(b.country_name),
      render: (text) => (
        <Text strong style={{ fontSize: "14px" }}>
          {text}
        </Text>
      ),
    },
    {
      title: "Flag",
      dataIndex: "flag",
      key: "flag",
      width: 70,
      filters: [
        { text: "Has flag", value: "true" },
        { text: "No flag", value: "false" },
      ],
      filterMultiple: false,
      filteredValue: filters.hasFlag != null ? [filters.hasFlag] : null,
      render: (flag, record) =>
        flag ? (
          <img
            src={getImageUrl(flag)}
            alt={`${record.country_name} flag`}
            height={16}
            style={{ display: "block" }}
          />
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
    {
      title: "Mobile Code",
      dataIndex: "country_mob_code",
      key: "country_mob_code",
      width: 130,
      render: (code) => <Text>{code || "-"}</Text>,
    },
    {
      title: "Mobile Visible",
      dataIndex: "country_mob_code_visible",
      key: "country_mob_code_visible",
      width: 140,
      filters: [
        { text: "Visible", value: "true" },
        { text: "Hidden", value: "false" },
      ],
      filterMultiple: false,
      filteredValue: filters.mobVisible != null ? [filters.mobVisible] : null,
      render: (visible) => (
        <Tag color={visible === true ? "green" : "default"}>
          {visible === true ? "Visible" : "Hidden"}
        </Tag>
      ),
    },
    {
      title: "Status",
      dataIndex: "country_is_active",
      key: "country_is_active",
      width: 120,
      filters: [
        { text: "Active", value: "Y" },
        { text: "Inactive", value: "N" },
      ],
      filterMultiple: false,
      // Controlled from the same state the header dropdown drives, so the
      // column funnel and the dropdown always agree.
      filteredValue: filters.status !== "all" ? [filters.status] : null,
      render: (status) => (
        <Tag color={status === "Y" ? "green" : "red"}>
          {status === "Y" ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "action",
      width: 100,
      fixed: "right",
      render: (text, record) => (
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() => handleEditCountry(record.country_id)}
          size="small"
        >
          Edit
        </Button>
      ),
    },
  ];

  // Header content with user info

  return (
    <Card>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "16px",
          gap: "16px",
          flexWrap: "wrap",
        }}
      >
        <Title level={4} style={{ margin: 0 }}>
          Countries ({pagination.total})
        </Title>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            minWidth: 300,
            flex: 1,
            justifyContent: "flex-end",
          }}
        >
          <Select
            value={filters.status}
            onChange={handleStatusChange}
            placeholder="All"
            style={{ width: 140 }}
          >
            <Option value="all">All</Option>
            <Option value="Y">Active</Option>
            <Option value="N">Inactive</Option>
          </Select>
          <Search
            placeholder="Search countries..."
            allowClear
            value={filters.searchText}
            onChange={handleSearchChange}
            onSearch={handleSearchNow}
            style={{ width: 320, maxWidth: "40vw" }}
            enterButton={<SearchOutlined />}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddCountry}
            size="middle"
          >
            Add Country
          </Button>
        </div>
      </div>

      <Table
        columns={columns}
        dataSource={countries}
        rowKey="country_id"
        loading={loading}
        pagination={pagination}
        onChange={handleTableChange}
        scroll={{ x: 800 }}
        size="middle"
        locale={{
          emptyText: "No countries found",
          filterConfirm: "OK",
          filterReset: "Reset",
          filterEmptyText: "No filters",
        }}
      />
    </Card>
  );
};

export default CountryList;
